import { useQuery, WatchQueryFetchPolicy } from '@apollo/client';

import {
  VirtualTryOnAppearanceQuery,
  VirtualTryOnAppearanceQueryVariables,
  VirtualTryOnProductsByLookIdQuery,
  VirtualTryOnProductsByLookIdQueryVariables,
  VirtualTryOnShadesQuery,
  VirtualTryOnShadesQueryVariables,
  VtoBeautyType,
} from '@notino/shared/definitions/types';
import type {
  EMakeupSupportedSdkVersion,
  EHairSupportedSdkVersion,
} from '@notino/virtual-try-on-lib';
import { IModifaceVisualizerProps } from '@notino/virtual-try-on-lib/dist/types/components/organisms/ModifaceVisualizer';

import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';

import vtoAppearanceQuery from './vtoAppearance.graphql';
import vtoProductsByLookId from './vtoProductsByLookId.graphql';
import vtoShadesQuery from './vtoShades.graphql';

const VTO_MAKEUP_VERSION = '9.0.4' as EMakeupSupportedSdkVersion; // dont want to use the enum value so the vto lib bundle is not included
const VTO_HAIR_VERSION = '8.8.4' as EHairSupportedSdkVersion;

export const getVtoVersionForVisualizer = (
  type: VtoBeautyType
): IModifaceVisualizerProps['version'] => {
  switch (type) {
    case 'Makeup':
      return {
        type: 'makeup',
        sdkVersion: VTO_MAKEUP_VERSION,
      };
    case 'Hair':
      return {
        type: 'hair' as const,
        sdkVersion: VTO_HAIR_VERSION,
      };
    default:
      throw new Error('Unsupported VtoBeautyType');
  }
};

export const useVirtualTryOnAppearance = ({
  disabled,
}: { disabled?: boolean } = {}) => {
  const { product } = useProductDetailContext();
  return useQuery<
    VirtualTryOnAppearanceQuery,
    VirtualTryOnAppearanceQueryVariables
  >(vtoAppearanceQuery, {
    variables: {
      catalogIds: product.variants.map((variant) => Number(variant.catalogId)),
      modifaceMakeupVersion: VTO_MAKEUP_VERSION,
      modifaceHairVersion: VTO_HAIR_VERSION,
    },
    skip: disabled,
    ssr: false,
  });
};

export const useVirtualTryOnShades = ({
  catalogIds,
  disabled,
}: {
  catalogIds: number[];
  disabled?: boolean;
}) =>
  useQuery<VirtualTryOnShadesQuery, VirtualTryOnShadesQueryVariables>(
    vtoShadesQuery,
    {
      variables: {
        catalogIds,
        modifaceMakeupVersion: VTO_MAKEUP_VERSION,
        modifaceHairVersion: VTO_HAIR_VERSION,
      },
      skip: disabled,
      ssr: false,
    }
  );

export const useVirtualTryOnProductsByLookId = ({
  lookId,
  disabled,
  fetchPolicy,
}: {
  lookId: number;
  disabled?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}) =>
  useQuery<
    VirtualTryOnProductsByLookIdQuery,
    VirtualTryOnProductsByLookIdQueryVariables
  >(vtoProductsByLookId, {
    variables: {
      lookId,
    },
    skip: disabled,
    fetchPolicy: fetchPolicy || 'network-only',
    ssr: false,
  });
